import React, { useState, useEffect, useMemo } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { SVGCertificateGenerator } from '../../lib/svgCertificateGenerator';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
// import KYCVerificationForm from '../kyc/KYCVerificationForm';
import KYCStatusCard from '../kyc/KYCStatusCard';
import { extractNumericId } from '../../lib/userIdExtractor';
import { DividendProjections2026to2035 } from '../dividends/DividendProjections2026to2035';
import { DIVIDEND_PROJECTIONS, CALCULATION_CONSTANTS, computeDynamicAnnualProjection } from '../../lib/services/calculationService';
import { useLiveTotalSharesSold } from '../../lib/hooks/useLiveTotalSharesSold';
import { DIVIDENDS_USER_VISIBLE } from '../../lib/dividendsUserVisible';
import { effectiveRowShares, isBonusOnlyRow } from '../../lib/shareRowDisplay';
import { acceptDisplayedSharePrice } from '../../lib/displayedSharePrice';
import { buildPortfolioHoldings, taxSummaryShareTotal } from '../../lib/taxSummaryHoldings';

// Single source of truth for share + mining constants
const __PLANT_CAPACITY_TPH = CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH;
const __EFFECTIVE_HOURS_PER_DAY = CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY;
const __OPERATING_DAYS_PER_YEAR = CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR;

// Default mining parameters (synced with calculationService)
const __DEFAULT_MINING_PARAMS = {
  avgGravelThickness: CALCULATION_CONSTANTS.DEFAULT_GRAVEL_THICKNESS_M,
  inSituGrade: CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE, // g recovered gold per tonne throughput
  recoveryFactor: CALCULATION_CONSTANTS.DEFAULT_RECOVERY_FACTOR_PERCENT, // recovery baked into grade
  goldPriceUsdPerKg: CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG, // $134,000/kg projection price
  opexPercent: CALCULATION_CONSTANTS.OPEX_PERCENT
};

// Aureus Alliance 5-year expansion plan
// const EXPANSION_PLAN = {
//   2026: { plants: 10, hectares: 250 },
//   2027: { plants: 30, hectares: 750 },
//   2028: { plants: 50, hectares: 1250 },
//   2029: { plants: 100, hectares: 2500 },
//   2030: { plants: 200, hectares: 5000 }
// };

interface PortfolioProps {
  user: any;
  currentPhase?: any;
  calculatorData?: any; // Dynamic calculator data from dashboard
  totalShares?: number; // Total shares from dashboard (includes all share types)
}

interface ShareHolding {
  id: string;
  shares_purchased: number;
  bonus_shares?: number;
  total_amount: number;
  price_per_share: number;
  phase_name: string;
  phase_number: number;
  purchase_date: string;
  status: string;
  transaction_reference?: string;
  transaction_type: 'purchase' | 'conversion' | 'commission' | 'transfer_sent' | 'transfer_received';
}

interface PortfolioSummary {
  totalShares: number;
  totalInvested: number;
  currentValue: number;
  unrealizedGains: number;
  unrealizedGainsPercent: number;
  averageCostPerShare: number;
  projectedAnnualDividend: number;
}

interface DividendRecord {
  id: string;
  amount: number;
  payment_date: string;
  status: string;
  type: string;
}

interface Certificate {
  id: string;
  certificate_number: string;
  shares: number;
  issue_date: string;
  status: 'issued' | 'pending' | 'revoked' | 'active';
  approval_status?: 'pending_approval' | 'approved' | 'rejected';
  download_url?: string;
  certificate_data?: any;
  is_frozen?: boolean;
  freeze_reason?: string | null;
  revoked_at?: string | null;
  revocation_reason?: string | null;
  shares_at_issuance?: number;
  is_company_certificate?: boolean;
  company_name?: string | null;
  company_registration_number?: string | null;
}

export const ComprehensivePortfolio: React.FC<PortfolioProps> = ({ user, currentPhase, calculatorData: _calculatorData, totalShares }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'certificates' | 'kyc' | 'transactions'>('overview');
  const [holdings, setHoldings] = useState<ShareHolding[]>([]);
  const [dividends, setDividends] = useState<DividendRecord[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState<string>('pending');
  const [kycData, setKycData] = useState<any>(null);
  const [showKYCForm, setShowKYCForm] = useState(false);
  const [frozenCertificate, setFrozenCertificate] = useState<Certificate | null>(null);
  const [openingCertificateId, setOpeningCertificateId] = useState<string | null>(null);

  // Certificate regeneration state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [hasHadApprovedCertificate, setHasHadApprovedCertificate] = useState(false);
  const [currentShareCount, _setCurrentShareCount] = useState(0);

  // Company certificate options
  const [isCompanyCertificate, setIsCompanyCertificate] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState('');

  // Certificate sub-tabs
  const [certificateTab, setCertificateTab] = useState<'active' | 'retracted'>('active');

  // Portfolio calculator inputs (same as dashboard calculator)
  const [calculatorInputs] = useState({
    landHa: 50, // Default to 50ha (same as dashboard fixed calculation)
    avgGravelThickness: 0.8,
    inSituGrade: 0.9,
    recoveryFactor: 70,
    goldPriceUsdPerKg: 128000, // Current market price (~$128/gram or $128,000/kg)
    opexPercent: 45,
    selectedYear: 2026
  });

  // Live total shares sold — dynamic denominator for Jul/Oct 2026 + Jan 2027
  // payouts. While shares are still selling the per-share dividend scales up
  // vs the 1,400,000-cap floor (e.g. ~141k sold → ~$64/share vs $6.48 floor).
  const { totalSharesSold: liveSharesSold } = useLiveTotalSharesSold();

  // Portfolio calculations using CORRECT Aureus Africa dividend formula
  const portfolioSummary = useMemo((): PortfolioSummary => {
    if (!holdings.length) {
      return {
        totalShares: 0,
        totalInvested: 0,
        currentValue: 0,
        unrealizedGains: 0,
        unrealizedGainsPercent: 0,
        averageCostPerShare: 0,
        projectedAnnualDividend: 0
      };
    }

    // effectiveRowShares folds bonus-only gift rows (shares_purchased=0,
    // bonus_shares>0) into their real amount so portfolio metrics don't undercount
    // gifts. Display-only; canonical equity total is unchanged.
    const purchasedShares = taxSummaryShareTotal(holdings);
    const totalInvested = holdings.reduce((sum, holding) => sum + holding.total_amount, 0);

    // Value holdings at the live phase price only. Never substitute $25.
    const currentPricePerShare = acceptDisplayedSharePrice(currentPhase?.price_per_share);
    const currentValue = currentPricePerShare == null ? 0 : purchasedShares * currentPricePerShare;

    const unrealizedGains = currentPricePerShare == null ? 0 : currentValue - totalInvested;
    const unrealizedGainsPercent = currentPricePerShare == null || totalInvested <= 0 ? 0 : (unrealizedGains / totalInvested) * 100;
    const averageCostPerShare = totalInvested > 0 ? totalInvested / purchasedShares : 0;

    // Calculate projected dividend using central DIVIDEND_PROJECTIONS.
    // First scheduled payout is July 2026 (3-month holding rule applies).
    const { goldPriceUsdPerKg, opexPercent } = calculatorInputs;

    // Apply live-denominator scaling for the 2026 ramp year so the projected
    // annual dividend reflects ~141k actual shares sold (not the 1.4M cap).
    const staticProj2026 = DIVIDEND_PROJECTIONS[2026];
    const dyn2026 = computeDynamicAnnualProjection(2026, liveSharesSold);
    const effectiveAnnualPerShare2026 = dyn2026
      ? dyn2026.estimatedDividendPerShare
      : (staticProj2026?.estimatedDividendPerShare || 0);
    const __firstPayoutPerShare = staticProj2026?.quarterlyPayouts?.[0]?.amount || 0;
    // Use totalShares prop from dashboard if available, otherwise fall back to purchased shares
    const actualTotalShares = totalShares || purchasedShares;
    const projectedAnnualDividend = effectiveAnnualPerShare2026 * actualTotalShares;
    const __dividendPerShare = effectiveAnnualPerShare2026;

    return {
      totalShares: actualTotalShares, // Use the actual total shares (including all types)
      totalInvested,
      currentValue,
      unrealizedGains,
      unrealizedGainsPercent,
      averageCostPerShare,
      projectedAnnualDividend
    };
  }, [holdings, currentPhase, calculatorInputs, totalShares, liveSharesSold]);

  const openCertificatePng = async (certificate: Certificate, mode: 'view' | 'download') => {
    if (certificate.is_frozen) {
      setFrozenCertificate(certificate);
      return;
    }
    setOpeningCertificateId(certificate.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'get_signed_certificate_url',
          data: {
            certificateId: certificate.id,
            path: certificate.download_url,
            expiresIn: 3600,
          },
        }),
      });
      const result = await response.json();
      const signedUrl = result?.data?.signedUrl;
      if (!response.ok || typeof signedUrl !== 'string' || !signedUrl) {
        alert('Could not open certificate. Please refresh the page and try again.');
        return;
      }
      if (mode === 'download') {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = `Certificate_${certificate.certificate_number}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(signedUrl, '_blank');
      }
    } catch {
      alert('Could not open certificate. Please refresh the page and try again.');
    } finally {
      setOpeningCertificateId(null);
    }
  };

  useEffect(() => {
    console.log('🔄 Portfolio component mounted with user:', user);
    console.log('📊 User database ID:', user?.database_user?.id);
    loadPortfolioData();
  }, [user]);

  const loadPortfolioData = async () => {
    console.log('🔄 Starting portfolio data load...');
    console.log('👤 User object:', user);
    console.log('🆔 Database user ID:', user?.database_user?.id);
    console.log('📧 User email:', user?.database_user?.email || user?.email);
    console.log('🔗 Account type:', user?.account_type);
    console.log('🔍 Full user structure:', JSON.stringify(user, null, 2));

    // CRITICAL FIX: Use extractNumericId to safely extract user ID
    // This prevents "invalid input syntax for type integer: 'db_db_410'" errors
    const userId = extractNumericId(user?.database_user?.id) ||
                   extractNumericId(user?.database_user?.telegram_id) ||
                   extractNumericId(user?.user_metadata?.user_id) ||
                   extractNumericId(user?.id);

    if (userId) {
      console.log('✅ Extracted numeric user ID:', userId);
    } else {
      console.log('⚠️ No valid user ID found, loading demo data for testing');
      console.log('🔍 Available user properties:', Object.keys(user || {}));

      // Load demo data for testing when no user is logged in
      const demoHoldings: ShareHolding[] = [
        {
          id: 'demo-1',
          shares_purchased: 1000,
          total_amount: 5000,
          price_per_share: 5.00,
          phase_name: 'Pre Sale',
          phase_number: 0,
          purchase_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          transaction_reference: 'DEMO-TX-001',
          transaction_type: 'purchase'
        },
        {
          id: 'demo-2',
          shares_purchased: 500,
          total_amount: 5000,
          price_per_share: 10.00,
          phase_name: 'Phase 1',
          phase_number: 1,
          purchase_date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          transaction_reference: 'DEMO-TX-002',
          transaction_type: 'purchase'
        }
      ];

      setHoldings(demoHoldings);
      setKycStatus('approved');

      // Set demo KYC data
      setKycData({
        id: 'demo-kyc-001',
        user_id: 999,
        first_name: 'Demo',
        last_name: 'User',
        full_legal_name: 'Demo User',
        id_type: 'national_id',
        phone_number: '+27 123 456 7890',
        email_address: 'demo@aureusafrica.com',
        country_name: 'South Africa',
        kyc_status: 'completed',
        kyc_completed_at: '2025-01-15T10:00:00Z',
        data_consent_given: true,
        privacy_policy_accepted: true
      });

      const demoCertificates = demoHoldings.map((holding, index) => ({
        id: holding.id,
        certificate_number: `AUR-DEMO-${String(index + 1).padStart(3, '0')}`,
        shares: holding.shares_purchased,
        issue_date: holding.purchase_date,
        status: 'issued' as const,
      }));

      setCertificates(demoCertificates);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔄 Loading portfolio data for user ID:', userId);

      // Load portfolio data via API
      const portfolioResponse = await fetch(`${API_BASE_URL}/api/user/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'load_portfolio_data' })
      });

      const portfolioResult = await portfolioResponse.json();
      if (!portfolioResponse.ok) {
        throw new Error(portfolioResult.error || 'Failed to load portfolio data');
      }

      const { purchases, commissionShares, transfersIn, transfersOut } = portfolioResult.data || {};

      const allHoldings = buildPortfolioHoldings({
        purchases,
        commissionShares,
        transfersIn,
        transfersOut,
      })
      setHoldings(allHoldings);

      console.log('📈 Total holdings loaded:', allHoldings.length);

      // Load certificates
      const certResponse = await fetch(`${API_BASE_URL}/api/user/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'load_certificates' })
      });

      const certResult = await certResponse.json();
      if (certResponse.ok && certResult.data?.certificates) {
        const realCertificates = certResult.data.certificates.map((cert: any) => ({
          id: cert.id,
          certificate_number: cert.certificate_number,
          shares: cert.shares_count,
          issue_date: cert.issue_date,
          status: cert.status,
          approval_status: cert.approval_status,
          download_url: cert.certificate_data?.file_url || null,
          certificate_data: cert.certificate_data,
          is_frozen: !!cert.is_frozen,
          freeze_reason: cert.freeze_reason ?? null,
          revoked_at: cert.revoked_at ?? null,
          revocation_reason: cert.revocation_reason ?? null,
          shares_at_issuance: cert.shares_at_issuance ?? cert.shares_count,
          is_company_certificate: !!cert.is_company_certificate,
          company_name: cert.company_name ?? null,
          company_registration_number: cert.company_registration_number ?? null
        }));
        setCertificates(realCertificates);
        setHasHadApprovedCertificate(certResult.data.certificates?.some((c: any) => c.approval_status === 'approved'));
      } else {
        // Fallback to mock certificates
        const mockCertificates = allHoldings?.map((holding: any, index: number) => ({
          id: holding.id,
          certificate_number: `AUR-${String(userId).padStart(4, '0')}-${String(index + 1).padStart(3, '0')}`,
          shares: holding.shares_purchased || 0,
          issue_date: holding.purchase_date,
          status: 'issued' as const,
        })) || [];
        setCertificates(mockCertificates);
      }

      setDividends([]);

      // Load KYC data via API
      const kycResponse = await fetch(`${API_BASE_URL}/api/user/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'load_kyc_data' })
      });

      const kycResult = await kycResponse.json();
      if (kycResponse.ok && kycResult.data?.kycInfo) {
        setKycData(kycResult.data.kycInfo);
        setKycStatus(kycResult.data.kycInfo.kyc_status || 'pending');
      } else {
        setKycStatus('pending');
        setKycData(null);
      }

    } catch (error) {
      console.error('❌ Error loading portfolio data:', error);
      setHoldings([]);
      setDividends([]);
      setCertificates([]);
      setKycStatus('pending');
      setKycData(null);
    } finally {
      setLoading(false);
    }
  };

  // Self-regenerate certificate (only for users who have had admin-approved certificates before)
  const handleRegenerateCertificate = async () => {
    if (!hasHadApprovedCertificate) {
      alert('You need to have your first certificate approved by admin before you can self-regenerate.');
      return;
    }

    // Extract user ID using the same logic as loadPortfolioData
    const userId = extractNumericId(user?.database_user?.id) ||
                   extractNumericId(user?.database_user?.telegram_id) ||
                   extractNumericId(user?.user_metadata?.user_id) ||
                   extractNumericId(user?.id);
    if (!userId) {
      console.error('❌ Unable to identify user for certificate regeneration:', user);
      alert('Unable to identify user. Please try again.');
      return;
    }
    console.log('✅ Regenerating certificate for user ID:', userId);

    setRegenerating(true);
    try {
      // Get user's current total shares from the dashboard totalShares prop
      const userCurrentShares = totalShares || 0;

      if (userCurrentShares < 1) {
        alert('You need at least 1 share to generate a certificate.');
        setRegenerating(false);
        return;
      }

      // Call API to regenerate certificate
      const response = await fetch(`${API_BASE_URL}/api/user/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'regenerate_certificate_full',
          data: {
            currentShares: userCurrentShares,
            kycData,
            isCompanyCertificate,
            companyName,
            companyRegistrationNumber
          }
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        console.error('Error regenerating certificate:', result.error);
        alert(result.error || 'Failed to regenerate certificate. Please contact support.');
        setRegenerating(false);
        return;
      }

      const { __newCert, certNo, wholeShares } = result.data;

      // Now generate the actual PNG certificate using the SVG template
      try {
        const svgGenerator = new SVGCertificateGenerator();

        // Get user's full name from KYC data
        const userName = kycData?.full_legal_name ||
                        (kycData?.first_name && kycData?.last_name
                          ? `${kycData.first_name} ${kycData.last_name}`
                          : null) ||
                        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                        user.username ||
                        'Shareholder';

        // Decrypt ID number from KYC (base64 encoded)
        let userIdNumber = 'N/A';
        if (kycData?.id_number_encrypted) {
          try {
            userIdNumber = atob(kycData.id_number_encrypted);
          } catch {
            userIdNumber = kycData.id_number_encrypted;
          }
        } else if (kycData?.id_number) {
          userIdNumber = kycData.id_number;
        }

        // Use KYC address fields directly
        const addressLine1 = kycData?.street_address || '';
        const addressLine2 = kycData?.province || '';
        const city = kycData?.city || '';
        const country = kycData?.country_name || '';
        const postalCode = kycData?.postal_code || '';

        const { pngUrl } = await svgGenerator.generateConsolidatedCertificatePNG({
          userId,
          certificateNumber: certNo,
          sharesCount: wholeShares,
          userName,
          userIdNumber,
          addressLine1,
          addressLine2,
          city,
          country,
          postalCode,
          issueDate: new Date().toLocaleDateString('en-GB')
        });

        if (pngUrl) {
          console.log('✅ Certificate PNG generated successfully:', pngUrl);
          alert(`✅ Certificate regenerated successfully!\n\nNew Certificate: ${certNo}\nShares: ${wholeShares}\n\nYour new certificate is ready! You can view and download it now.`);
        } else {
          console.warn('⚠️ Certificate record created but PNG generation failed');
          alert(`⚠️ Certificate record created but PNG generation failed.\n\nCertificate: ${certNo}\nShares: ${wholeShares}\n\nPlease contact support to regenerate the PNG.`);
        }
      } catch (pngError) {
        console.error('Error generating certificate PNG:', pngError);
        alert(`⚠️ Certificate record created but PNG generation failed.\n\nCertificate: ${certNo}\nShares: ${wholeShares}\n\nPlease contact support to regenerate the PNG.`);
      }

      // Reload certificates
      setShowRegenerateModal(false);
      loadPortfolioData();

    } catch (error) {
      console.error('Error regenerating certificate:', error);
      alert('Failed to regenerate certificate. Please try again or contact support.');
    } finally {
      setRegenerating(false);
    }
  };

  // Export functionality for transactions
  const exportTransactionsPDF = async () => {
    try {
      const transactions = holdings || [];
      if (transactions.length === 0) {
        alert('No transactions to export.');
        return;
      }

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Load and add logo
      try {
        const logoUrl = 'https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png';
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const logoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        // Add logo image
        doc.addImage(logoBase64, 'PNG', 20, 15, 25, 25);

        // Add company name next to logo
        doc.setFontSize(20);
        doc.setTextColor(212, 175, 55); // Gold color
        doc.text('AUREUS AFRICA', 50, 30);
      } catch (error) {
        console.warn('Could not load logo, using text fallback:', error);
        // Fallback to text-only header
        doc.setFontSize(20);
        doc.setTextColor(212, 175, 55); // Gold color
        doc.text('🏆 AUREUS AFRICA', 20, 25);
      }

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Transaction Report', 20, 50);

      doc.setFontSize(10);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 60);

      // Add user info
      doc.setFontSize(12);
      doc.text(`Account: ${user.username || 'N/A'}`, 20, 75);
      doc.text(`Email: ${user.email || 'N/A'}`, 20, 85);
      doc.text(`Total Transactions: ${transactions.length}`, 20, 95);

      // Add table header
      let yPosition = 115;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', 20, yPosition);
      doc.text('Type', 50, yPosition);
      doc.text('Description', 80, yPosition);
      doc.text('Shares', 130, yPosition);
      doc.text('Amount', 150, yPosition);
      doc.text('Status', 180, yPosition);

      // Add line under header
      doc.line(20, yPosition + 2, 200, yPosition + 2);
      yPosition += 10;

      // Add transaction data
      doc.setFont('helvetica', 'normal');
      transactions.forEach((tx: any) => {
        if (yPosition > 270) { // Start new page if needed
          doc.addPage();
          yPosition = 20;
        }

        doc.text(new Date(tx.purchase_date).toLocaleDateString(), 20, yPosition);
        doc.text('Purchase', 50, yPosition);
        doc.text(`${tx.phase_name || 'Pre Sale Purchase'}`, 80, yPosition);
        doc.text(`+${effectiveRowShares(tx)}`, 130, yPosition);
        doc.text(`$${tx.total_amount}`, 150, yPosition);
        doc.text(tx.status, 180, yPosition);
        yPosition += 8;
      });

      // Add summary
      yPosition += 10;
      doc.setFont('helvetica', 'bold');
      const totalShares = taxSummaryShareTotal(transactions);
      const totalInvested = transactions.reduce((sum: number, tx: any) => sum + tx.total_amount, 0);

      doc.text(`Total Shares: ${totalShares}`, 20, yPosition);
      doc.text(`Total Purchased: $${totalInvested.toFixed(2)}`, 20, yPosition + 10);

      // Save the PDF
      doc.save(`aureus-transactions-${user.username || 'user'}-${new Date().toISOString().split('T')[0]}.pdf`);

      alert('PDF report exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const exportTransactionsExcel = async () => {
    try {
      const transactions = holdings || [];
      if (transactions.length === 0) {
        alert('No transactions to export.');
        return;
      }

      // Create CSV content
      const csvHeaders = 'Date,Type,Description,Shares,Amount,Status,Payment Method,Reference ID\n';
      const csvRows = transactions.map(tx => {
        const date = new Date(tx.purchase_date).toLocaleDateString();
        const type = 'Purchase';
        const description = `Share Purchase - ${tx.phase_name || 'Pre Sale Purchase'}`;
        const shares = `+${effectiveRowShares(tx)}`;
        const amount = `$${tx.total_amount}`;
        const status = tx.status;
        const paymentMethod = 'N/A'; // Not available in holdings data
        const referenceId = tx.transaction_reference || tx.id || 'N/A';

        return `"${date}","${type}","${description}","${shares}","${amount}","${status}","${paymentMethod}","${referenceId}"`;
      }).join('\n');

      const csvContent = csvHeaders + csvRows;

      // Create and download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aureus-transactions-${user.username || 'user'}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('Transaction data exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const exportTaxSummary = async () => {
    try {
      const transactions = holdings || [];
      if (transactions.length === 0) {
        alert('No transactions to export.');
        return;
      }

      const currentYear = new Date().getFullYear();
      const totalInvested = transactions.reduce((sum, tx) => sum + tx.total_amount, 0);
      // Same total as Equity Shares (Owned): no Referral Commission Shares add,
      // no leftover zeroed conversion / Package line. Display-only.
      const totalShares = taxSummaryShareTotal(transactions);

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Load and add logo
      try {
        const logoUrl = 'https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png';
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const logoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        // Add logo image
        doc.addImage(logoBase64, 'PNG', 20, 15, 25, 25);

        // Add company name next to logo
        doc.setFontSize(20);
        doc.setTextColor(212, 175, 55); // Gold color
        doc.text('AUREUS AFRICA', 50, 30);
      } catch (error) {
        console.warn('Could not load logo, using text fallback:', error);
        // Fallback to text-only header
        doc.setFontSize(20);
        doc.setTextColor(212, 175, 55); // Gold color
        doc.text('🏆 AUREUS AFRICA', 20, 25);
      }

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(`Tax Summary Report - ${currentYear}`, 20, 50);

      doc.setFontSize(10);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 60);

      // Add share purchase summary box
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Share Purchase Summary', 20, 80);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Account Holder: ${user.username || 'N/A'}`, 20, 95);
      doc.text(`Email: ${user.email || 'N/A'}`, 20, 105);
      doc.text(`Total Shares Purchased: ${totalShares}`, 20, 115);
      doc.text(`Total Amount Purchased: $${totalInvested.toFixed(2)}`, 20, 125);
      doc.text(`Number of Transactions: ${transactions.length}`, 20, 135);

      // Add important tax information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('⚠️ Important Tax Information', 20, 155);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('This summary is for informational purposes only.', 20, 165);
      doc.text('Please consult with a qualified tax professional for specific tax advice.', 20, 175);
      doc.text('Share ownership may have tax implications depending on your jurisdiction.', 20, 185);

      // Add transaction details table
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Transaction Details', 20, 205);

      // Table header
      let yPosition = 220;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', 20, yPosition);
      doc.text('Description', 50, yPosition);
      doc.text('Shares', 120, yPosition);
      doc.text('Amount', 150, yPosition);
      doc.text('Status', 180, yPosition);

      // Add line under header
      doc.line(20, yPosition + 2, 200, yPosition + 2);
      yPosition += 10;

      // Add transaction data
      doc.setFont('helvetica', 'normal');
      transactions.forEach((tx: any) => {
        if (yPosition > 270) { // Start new page if needed
          doc.addPage();
          yPosition = 20;
        }

        doc.text(new Date(tx.purchase_date).toLocaleDateString(), 20, yPosition);
        doc.text(`${tx.phase_name || 'Pre Sale Purchase'}`, 50, yPosition);
        doc.text(`${effectiveRowShares(tx)}`, 120, yPosition);
        doc.text(`$${tx.total_amount.toFixed(2)}`, 150, yPosition);
        doc.text(tx.status, 180, yPosition);
        yPosition += 8;
      });

      // Add footer
      yPosition += 20;
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('This report was generated by Aureus Africa Holdings (Pty) Ltd', 20, yPosition);
      doc.text('For questions, contact support@aureusafrica.com', 20, yPosition + 10);

      // Save the PDF
      doc.save(`aureus-tax-summary-${user.username || 'user'}-${currentYear}.pdf`);

      alert('Tax summary PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting tax summary:', error);
      alert('Failed to export tax summary. Please try again.');
    }
  };

  const renderKYCTab = () => (
    <div className="space-y-6">
      <KYCStatusCard
        status={kycStatus as any}
        kycData={kycData}
        onStartKYC={() => {
          if (kycStatus === 'pending') {
            // Navigate to dashboard KYC section
            window.location.href = '/dashboard';
          }
        }}
        onEditKYC={() => {
          if (kycStatus === 'completed' || kycStatus === 'rejected' || kycStatus === 'expired') {
            // Navigate to dashboard KYC section
            window.location.href = '/dashboard';
          }
        }}
      />

      {/* Remove the legacy KYC form - users should go to dedicated KYC page */}
      {showKYCForm && kycStatus === 'pending' && (
        <div className="bg-blue-900/30 rounded-lg p-6 border border-blue-700">
          <div className="flex items-center space-x-3 mb-4">
            <span className="text-3xl">🚀</span>
            <div>
              <h3 className="text-blue-400 font-medium text-lg">Redirecting to KYC Certification</h3>
              <p className="text-blue-200 text-sm">
                You'll be redirected to our comprehensive KYC verification system with document upload and facial recognition.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/kyc-certification'}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Continue to KYC Certification →
          </button>
        </div>
      )}

      {showKYCForm && kycStatus !== 'pending' && (
        <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-700">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h3 className="text-yellow-400 font-medium">KYC Already Submitted</h3>
              <p className="text-yellow-200 text-sm mt-1">
                Your KYC information has already been submitted. Please wait for admin approval.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowKYCForm(false)}
            className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );

  const renderCertificatesTab = () => {
    // Separate certificates by status
    const activeCertificates = certificates.filter(c => c.status === 'issued' || c.status === 'active');
    const retractedCertificates = certificates.filter(c => c.status === 'revoked');
    const displayedCertificates = certificateTab === 'active' ? activeCertificates : retractedCertificates;

    // Check eligibility for consolidated certificate generation
    const isKycApproved = kycStatus === 'completed' || kycStatus === 'approved';
    const hasMinimumShares = (totalShares || 0) >= 1;
    const __canGenerateCertificate = isKycApproved && hasMinimumShares && hasHadApprovedCertificate;
    const needsNewCertificate = activeCertificates.length === 0 ||
      Math.abs((totalShares || 0) - currentShareCount) > 0.01;

    return (
    <div className="space-y-6">
      {/* KYC Status Notice */}
      {kycStatus !== 'completed' && kycStatus !== 'approved' && (
        <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-yellow-400 text-xl">⚠️</span>
              <div>
                <h4 className="text-yellow-400 font-semibold">KYC Verification Required</h4>
                <p className="text-yellow-200 text-sm">
                  Complete your KYC verification to access and download your share certificates.
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('kyc')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Complete KYC
            </button>
          </div>
        </div>
      )}

      {/* Consolidated Certificate Summary Card */}
      <div className="bg-gradient-to-r from-gold-900/20 via-gray-800 to-gold-900/20 rounded-xl p-4 sm:p-6 border border-gold-500/30 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl sm:text-2xl">🏆</span>
              </div>
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-white truncate">Consolidated Share Holdings</h3>
                <p className="text-white text-xs sm:text-sm">Your total ownership in Aureus Africa</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mt-4">
              <div className="bg-gray-900/50 rounded-lg p-2.5 sm:p-3 border border-gray-700">
                <p className="text-white text-xs sm:text-sm uppercase tracking-wide">Total Shares</p>
                <p className="text-lg sm:text-2xl font-bold text-green-400">{(totalShares || 0).toFixed(2)}</p>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-2.5 sm:p-3 border border-gray-700">
                <p className="text-white text-xs sm:text-sm uppercase tracking-wide">Active Certs</p>
                <p className="text-lg sm:text-2xl font-bold text-blue-400">{activeCertificates.length}</p>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-2.5 sm:p-3 border border-gray-700">
                <p className="text-white text-xs sm:text-sm uppercase tracking-wide">Certified</p>
                <p className="text-lg sm:text-2xl font-bold text-yellow-400">{currentShareCount.toFixed(2)}</p>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-2.5 sm:p-3 border border-gray-700">
                <p className="text-white text-xs sm:text-sm uppercase tracking-wide">Retracted</p>
                <p className="text-lg sm:text-2xl font-bold text-amber-400">{retractedCertificates.length}</p>
              </div>
            </div>

            {/* Status indicator */}
            {needsNewCertificate && (totalShares || 0) > 0 && (
              <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <span>⚠️</span>
                <span>Your share count has changed. Consider generating an updated certificate.</span>
              </div>
            )}
          </div>

          <div className="lg:ml-6 flex flex-col items-center lg:items-end gap-2">
            <button
              onClick={() => alert('⚠️ Certificate generation is temporarily disabled.\n\nAdmin will handle certificate generation until this feature is re-enabled.\n\nPlease contact support if you need a certificate generated.')}
              className="px-6 py-3 rounded-lg font-semibold text-sm transition-all bg-gray-700 text-gray-400 cursor-not-allowed"
            >
              📜 Generate Consolidated Certificate
            </button>
            <p className="text-amber-500 text-sm text-center lg:text-right max-w-[220px]">
              🔒 Temporarily disabled - Admin will generate certificates
            </p>
          </div>
        </div>
      </div>

      {/* Certificate Sub-Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-700 pb-0">
        <button
          onClick={() => setCertificateTab('active')}
          className={`px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
            certificateTab === 'active'
              ? 'text-green-400 border-green-400 bg-green-900/20'
              : 'text-white border-transparent hover:text-white hover:border-gray-600'
          }`}
        >
          ✅ Active Certificates ({activeCertificates.length})
        </button>
        <button
          onClick={() => setCertificateTab('retracted')}
          className={`px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
            certificateTab === 'retracted'
              ? 'text-amber-400 border-amber-400 bg-amber-900/20'
              : 'text-white border-transparent hover:text-white hover:border-gray-600'
          }`}
        >
          📋 Retracted Certificates ({retractedCertificates.length})
        </button>
      </div>

      {/* Certificate Status Warning - only show on active tab when no certs */}
      {certificateTab === 'active' && activeCertificates.length === 0 && holdings.length > 0 && (
        <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">📜</span>
            <div>
              <h4 className="text-blue-400 font-semibold">Certificates Pending</h4>
              <p className="text-blue-200 text-sm">
                Your share certificates are being prepared by our admin team.
                You will be able to download them once they are issued.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Certificates Grid */}
      {displayedCertificates.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
          <div className="text-6xl mb-4">{certificateTab === 'active' ? '📜' : '📋'}</div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {certificateTab === 'active' ? 'No Active Certificates' : 'No Retracted Certificates'}
          </h3>
          <p className="text-white mb-4">
            {certificateTab === 'active'
              ? (holdings.length === 0
                ? "You don't have any share purchases yet."
                : "Your certificates are being prepared by our admin team.")
              : "You don't have any retracted certificates. This is good!"}
          </p>
          {certificateTab === 'active' && holdings.length > 0 && (
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
              <p className="text-blue-200 text-sm">
                💡 <strong>Note:</strong> Certificates are manually issued by admin after share purchase approval.
                Please contact support if you have approved purchases but no certificates after 24 hours.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedCertificates.map((certificate) => {
            const isKycApproved = kycStatus === 'completed' || kycStatus === 'approved';
            const __isFrozen = !!certificate.is_frozen;
            const isRetracted = certificate.status === 'revoked';
            const isActive = certificate.status === 'issued' || certificate.status === 'active';

            return (
              <div
                key={certificate.id}
                className={`bg-gray-800 rounded-lg p-6 border ${
                  isRetracted
                    ? 'border-amber-500/50 bg-amber-900/10'
                    : 'border-gray-700'
                }`}
              >
                <div className="text-center mb-4">
                  <div className="text-3xl mb-2">{isRetracted ? '📋' : '📜'}</div>
                  <h4 className="text-lg font-semibold text-white">
                    Certificate #{certificate.certificate_number}
                  </h4>
                  <p className="text-white text-sm">
                    {certificate.shares.toLocaleString()} shares
                  </p>
                  {certificate.is_company_certificate && certificate.company_name && (
                    <p className="text-blue-400 text-sm mt-1">
                      🏢 Company: {certificate.company_name}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white">Issue Date:</span>
                    <span className="text-white">
                      {new Date(certificate.issue_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white">Status:</span>
                    <span
                      className={`font-semibold ${
                        isActive
                          ? 'text-green-400'
                          : isRetracted
                          ? 'text-amber-400'
                          : certificate.status === 'pending'
                          ? 'text-yellow-400'
                          : 'text-white'
                      }`}
                    >
                      {isRetracted ? '⚠️ Retracted' : isActive ? '✅ Active' : certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)}
                    </span>
                  </div>
                  {/* Retraction info - shown with amber styling */}
                  {isRetracted && certificate.revoked_at && (
                    <div className="flex justify-between">
                      <span className="text-white">Retracted On:</span>
                      <span className="text-amber-300 text-sm">
                        {new Date(certificate.revoked_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {isRetracted && certificate.revocation_reason && (
                    <div className="mt-2 p-2 bg-amber-900/30 border border-amber-500/30 rounded text-sm">
                      <span className="text-amber-400 font-semibold">Retraction Reason:</span>
                      <p className="text-amber-200 mt-1">{certificate.revocation_reason}</p>
                    </div>
                  )}
                  {certificate.is_frozen && (
                    <div className="flex justify-between">
                      <span className="text-white">Freeze Status:</span>
                      <span className="text-blue-300 font-semibold text-sm">❄️ Frozen - View/Download disabled</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-white">Certificate ID:</span>
                    <span className="text-white font-mono text-sm">
                      {certificate.certificate_number}
                    </span>
                  </div>
                  {certificate.download_url && !isRetracted && (
                    <div className="flex justify-between">
                      <span className="text-white">Type:</span>
                      <span className="text-blue-400 font-semibold text-sm">🏛️ Admin Generated</span>
                    </div>
                  )}
                  {certificate.certificate_data?.generation_metadata?.securityHash && (
                    <div className="flex justify-between">
                      <span className="text-white">Security Hash:</span>
                      <span className="text-green-400 font-mono text-sm">
                        {certificate.certificate_data.generation_metadata.securityHash}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  {certificate.download_url ? (
                    // Admin-generated certificate with PNG download
                    <>
                      <button
                        onClick={() => {
                          void openCertificatePng(certificate, 'view');
                        }}
                        disabled={!isKycApproved || openingCertificateId === certificate.id}
                        className={`w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                          isRetracted
                            ? 'bg-gray-600 hover:bg-gray-500'
                            : 'bg-blue-600 hover:bg-blue-500'
                        }`}
                      >
                        {openingCertificateId === certificate.id
                          ? 'Opening…'
                          : isRetracted
                            ? '📋 View Historical Certificate'
                            : '🖼️ View Certificate (PNG)'}
                      </button>
                      {!isRetracted && (
                        <button
                          onClick={() => {
                            void openCertificatePng(certificate, 'download');
                          }}
                          disabled={!isKycApproved || openingCertificateId === certificate.id}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {openingCertificateId === certificate.id ? 'Opening…' : '📥 Download PNG'}
                        </button>
                      )}
                    </>
                  ) : (
                    // Certificate PNG not yet generated - show pending message
                    <div className="text-center py-3">
                      <p className="text-amber-400 text-sm mb-2">
                        ⏳ Certificate PNG pending generation
                      </p>
                      <p className="text-white text-sm">
                        {certificate.approval_status === 'pending_approval'
                          ? 'Awaiting admin approval before certificate can be generated.'
                          : 'Please regenerate your certificate to create the PNG file.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {frozenCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg max-w-md w-full p-6 border border-yellow-700 shadow-2xl">
            <h3 className="text-xl font-bold text-yellow-300 mb-4 flex items-center space-x-2">
              <span>⚠️</span>
              <span>Certificate Frozen</span>
            </h3>
            <p className="text-white mb-3">
              This certificate is currently frozen due to:{' '}
              <span className="font-semibold">
                {frozenCertificate.freeze_reason || 'No reason provided.'}
              </span>
            </p>
            <p className="text-white mb-6">
              Please contact support or JP Rademeyer - CTO of Aureus Alliance Holdings at +27783699799 to arrange
              unfreezing of your certificates.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setFrozenCertificate(null)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Certificate Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg max-w-lg w-full p-6 border border-blue-500/50 shadow-2xl mx-4">
            <h3 className="text-xl font-bold text-blue-400 mb-4 flex items-center space-x-2">
              <span>🔄</span>
              <span>Regenerate Certificate</span>
            </h3>

            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <strong className="text-white">Current Shares:</strong> {(totalShares || 0).toFixed(2)} shares
                </p>
                <p className="text-sm text-gray-300 mt-1">
                  <strong className="text-white">Previously Certified:</strong> {currentShareCount.toFixed(2)} shares
                </p>
                {(totalShares || 0) !== currentShareCount && (
                  <p className="text-sm text-amber-400 mt-2">
                    ⚠️ Your share count has changed. A new certificate will reflect the updated amount.
                  </p>
                )}
              </div>

              {/* Company Certificate Option */}
              <div className="bg-gray-800 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isCompanyCertificate}
                    onChange={(e) => setIsCompanyCertificate(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-500 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-white font-medium">🏢 Generate Company Certificate</span>
                </label>
                <p className="text-white text-sm mt-1 ml-8">
                  Issue certificate in your company's name instead of personal name
                </p>
              </div>

              {isCompanyCertificate && (
                <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter company name"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Company Registration Number *
                    </label>
                    <input
                      type="text"
                      value={companyRegistrationNumber}
                      onChange={(e) => setCompanyRegistrationNumber(e.target.value)}
                      placeholder="Enter registration number"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                    <p className="text-gray-500 text-sm mt-1">
                      This will appear on the certificate instead of your ID number
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 text-sm">
                <p className="text-amber-200">
                  <strong>⚠️ Note:</strong> Your existing certificate(s) will be retracted and marked as historical records.
                  The retracted certificates will still be visible in your portfolio for reference.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowRegenerateModal(false);
                  setIsCompanyCertificate(false);
                  setCompanyName('');
                  setCompanyRegistrationNumber('');
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-sm"
                disabled={regenerating}
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateCertificate}
                disabled={regenerating || (isCompanyCertificate && (!companyName || !companyRegistrationNumber))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regenerating ? '⏳ Regenerating...' : '✅ Confirm Regeneration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Information */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-4">📋 Certificate Information</h4>
        <div className="space-y-3 text-sm">
          <div>
            <h5 className="text-blue-400 font-semibold mb-1">What are Share Certificates?</h5>
            <p className="text-gray-300">
              Digital share certificates are official documents that prove your ownership of Aureus Africa shares.
              Each certificate includes your name, share quantity, unique certificate number, and official company seal.
            </p>
          </div>
          <div>
            <h5 className="text-blue-400 font-semibold mb-1">Certificate Features:</h5>
            <ul className="text-gray-300 space-y-1 ml-4">
              <li>• Unique certificate numbers for verification</li>
              <li>• QR codes for authenticity checking</li>
              <li>• Professional PDF format for printing</li>
              <li>• Legally recognized ownership documents</li>
            </ul>
          </div>
          <div>
            <h5 className="text-blue-400 font-semibold mb-1">Requirements:</h5>
            <p className="text-gray-300">
              KYC verification must be completed before certificates can be issued or downloaded.
              This ensures compliance with regulatory requirements and protects your holdings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Conservative Dividend Projections 2026-2035 */}
      {DIVIDENDS_USER_VISIBLE && (
      <DividendProjections2026to2035 userShares={totalShares || portfolioSummary.totalShares} />
      )}

      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h4 className="text-lg font-semibold text-white mb-4">💰 Portfolio Metrics</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-white">Total Purchased:</span>
              <span className="text-white font-semibold">
                ${portfolioSummary.totalInvested.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white">Average Cost/Share:</span>
              <span className="text-white font-semibold">
                ${portfolioSummary.averageCostPerShare.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white">Current Price/Share:</span>
              <span className="text-white font-semibold">
                {acceptDisplayedSharePrice(currentPhase?.price_per_share) == null ? '...' : `$${acceptDisplayedSharePrice(currentPhase?.price_per_share)!.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white">Share Value Change:</span>
              <span className={`font-semibold ${portfolioSummary.unrealizedGainsPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {acceptDisplayedSharePrice(currentPhase?.price_per_share) == null ? '...' : `${portfolioSummary.unrealizedGainsPercent.toFixed(1)}%`}
              </span>
            </div>
          </div>
        </div>

        {DIVIDENDS_USER_VISIBLE && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h4 className="text-lg font-semibold text-white mb-4">📈 Dividend Projections</h4>
          {(() => {
            // Live-denominator overlay for the entire 2026 projection card. While
            // shares are still selling (~141k of 1.4M cap), Jul/Oct 2026 payouts
            // divide net profit by actual shares sold, not by the cap, so the
            // per-share figure scales up materially.
            const staticProj = DIVIDEND_PROJECTIONS[2026];
            const dyn = computeDynamicAnnualProjection(2026, liveSharesSold);
            const octAmt = dyn?.quarterlyPayouts?.find(p => p.month === 'October')?.amount ?? 0;
            const annualPerShare = dyn?.estimatedDividendPerShare ?? 0;
            const denominator = dyn ? Math.floor(dyn.denominatorUsed) : 0;
            return (
              <>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white">Dividend per Share (Annual):</span>
                    <span className="text-green-400 font-semibold">
                      ${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white">Projected Annual Dividend:</span>
                    <span className="text-green-400 font-semibold">
                      ${(annualPerShare * portfolioSummary.totalShares).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-900/30 rounded-lg border border-blue-700">
                  <p className="text-blue-200 text-sm mb-2">
                    📐 <strong>How dividends are calculated:</strong>
                  </p>
                  <ul className="text-blue-200 text-sm space-y-1 ml-4">
                    <li>• Gold production: <strong className="text-white">{staticProj.goldProductionKgPerYear.toLocaleString()} kg{staticProj.isRampYear ? ` in 2026 (${staticProj.goldProductionKgPerMonth} kg/month × ${staticProj.monthsOperating} operating months)` : '/year'}</strong></li>
                    <li>• Gold price: <strong className="text-white">${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</strong></li>
                    <li>• Net margin (after {CALCULATION_CONSTANTS.REFINING_PERCENT}% refining + {CALCULATION_CONSTANTS.OPEX_PERCENT}% opex): <strong className="text-white">50%</strong></li>
                    {dyn ? (
                      <li>• Shares used in calculation: <strong className="text-green-400">{denominator.toLocaleString()} shares sold so far</strong></li>
                    ) : (
                      <li>• Shares used in calculation: <strong className="text-white">Waiting for the live share count</strong></li>
                    )}
                    <li>• Per-share payout (October): <strong className="text-green-400">${octAmt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></li>
                    <li>• Annual dividend per share: <strong className="text-green-400">${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></li>
                  </ul>
                  <p className="text-blue-200 text-sm mt-2 pt-2 border-t border-blue-600">
                    Your shares: <strong className="text-white">{portfolioSummary.totalShares.toLocaleString(undefined, { maximumFractionDigits: 6 })}</strong> × ${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })} = <strong className="text-yellow-400">${(annualPerShare * portfolioSummary.totalShares).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                  </p>
                  <p className="text-blue-300 text-xs mt-1 italic">{staticProj.note}</p>
                  <div className="mt-3 p-2 bg-red-900/30 rounded border border-red-700">
                    <p className="text-red-200 text-sm">
                      ⚠️ <strong>IMPORTANT:</strong> These are projections based on gold production estimates.
                      Aureus Africa offers NO guaranteed returns or percentage yields. All dividends depend
                      entirely on actual gold extracted and sold. Mining operations involve significant risks.
                    </p>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
        )}
      </div>
    </div>
  );

  const renderTransactionsTab = () => (
    <div className="space-y-6">
      {/* Transaction Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-blue-400">
            {holdings.length}
          </div>
          <div className="text-sm text-white">Share Purchases</div>
        </div>
        {DIVIDENDS_USER_VISIBLE && (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-green-400">
            {dividends.length}
          </div>
          <div className="text-sm text-white">Dividend Payments</div>
        </div>
        )}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-2xl font-bold text-yellow-400">
            ${portfolioSummary.totalInvested.toLocaleString()}
          </div>
          <div className="text-sm text-white">Total Purchased</div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">📋 Transaction History</h3>
        </div>

        {holdings.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-white text-lg mb-4">📋</div>
            <h4 className="text-lg font-semibold text-white mb-2">No Transactions Yet</h4>
            <p className="text-white">
              Your share purchases will appear here.
            </p>
          </div>
        ) : (
          <>
          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {holdings.map((holding) => (
              <div key={holding.id} className="bg-gray-700/30 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{new Date(holding.purchase_date).toLocaleDateString()}</span>
                  <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">{holding.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs">Purchase</span>
                  <span className="text-white font-semibold text-sm">{isBonusOnlyRow(holding) ? '🎁 ' : '+'}{effectiveRowShares(holding).toLocaleString()} shares</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 text-xs">{holding.phase_name}</span>
                  <span className="text-gray-300">${holding.total_amount.toLocaleString()}</span>
                </div>
              </div>
            ))}
            {DIVIDENDS_USER_VISIBLE && dividends.map((dividend) => (
              <div key={dividend.id} className="bg-gray-700/30 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{new Date(dividend.payment_date).toLocaleDateString()}</span>
                  <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">{dividend.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">Dividend</span>
                  <span className="text-green-400 font-semibold text-sm">+${dividend.amount.toLocaleString()}</span>
                </div>
                <div className="text-gray-400 text-xs">{dividend.type} Dividend Payment</div>
              </div>
            ))}
          </div>
          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2 text-white">Date</th>
                  <th className="text-left py-2 text-white">Type</th>
                  <th className="text-left py-2 text-white">Description</th>
                  <th className="text-left py-2 text-white">Shares</th>
                  <th className="text-left py-2 text-white">Amount</th>
                  <th className="text-left py-2 text-white">Status</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <tr key={holding.id} className="border-b border-gray-700/50">
                    <td className="py-3 text-gray-300">
                      {new Date(holding.purchase_date).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-blue-600 text-white rounded text-sm">
                        Purchase
                      </span>
                    </td>
                    <td className="py-3 text-gray-300">
                      Share Purchase - {holding.phase_name}
                    </td>
                    <td className="py-3 text-white font-semibold">
                      {isBonusOnlyRow(holding) ? '🎁 ' : '+'}{effectiveRowShares(holding).toLocaleString()}
                    </td>
                    <td className="py-3 text-gray-300">
                      ${holding.total_amount.toLocaleString()}
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-green-600 text-white rounded text-sm">
                        {holding.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {DIVIDENDS_USER_VISIBLE && dividends.map((dividend) => (
                  <tr key={dividend.id} className="border-b border-gray-700/50">
                    <td className="py-3 text-gray-300">
                      {new Date(dividend.payment_date).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-green-600 text-white rounded text-sm">
                        Dividend
                      </span>
                    </td>
                    <td className="py-3 text-gray-300">
                      {dividend.type} Dividend Payment
                    </td>
                    <td className="py-3 text-white">-</td>
                    <td className="py-3 text-green-400 font-semibold">
                      +${dividend.amount.toLocaleString()}
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-green-600 text-white rounded text-sm">
                        {dividend.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Export Options */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-4">📊 Export Options</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={exportTransactionsPDF}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 text-center transition-colors duration-200"
          >
            <div className="text-2xl mb-2">📄</div>
            <div className="text-white font-semibold">PDF Report</div>
            <div className="text-white text-sm">Complete transaction history</div>
          </button>
          <button
            onClick={exportTransactionsExcel}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 text-center transition-colors duration-200"
          >
            <div className="text-2xl mb-2">📊</div>
            <div className="text-white font-semibold">Excel Export</div>
            <div className="text-white text-sm">Spreadsheet format</div>
          </button>
          <button
            onClick={exportTaxSummary}
            className="p-4 bg-gray-700 rounded-lg hover:bg-gray-600 text-center transition-colors duration-200"
          >
            <div className="text-2xl mb-2">🧾</div>
            <div className="text-white font-semibold">Tax Summary</div>
            <div className="text-white text-sm">For tax reporting</div>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-2xl font-bold text-white mb-2">📊 My Portfolio</h2>
        <p className="text-white">
          Comprehensive view of your Aureus Africa share holdings and performance
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-700">
        {[
          { key: 'overview', label: 'Calculator', icon: '📊' },
          { key: 'certificates', label: 'Certificates', icon: '📜' },
          { key: 'kyc', label: 'KYC', icon: '🔐' },
          { key: 'transactions', label: 'Txns', icon: '📋' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex-1 p-2.5 sm:p-4 text-center font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
            }`}
          >
            <span className="block sm:inline sm:mr-2 mb-0.5 sm:mb-0 text-base sm:text-base">{tab.icon}</span>
            <span className="text-xs sm:text-sm md:text-base block sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-3 sm:p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-white text-lg mb-4">⏳</div>
            <p className="text-white">Loading portfolio data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'certificates' && renderCertificatesTab()}
            {activeTab === 'kyc' && renderKYCTab()}
            {activeTab === 'transactions' && renderTransactionsTab()}
          </>
        )}
      </div>
    </div>
  );
};
